import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Home } from "lucide-react";

import type { Household } from "@/types/household";

type Props = {
  households: Household[];
  unassessed?: number;
};

function healthColor(score: number | null) {
  if (score === null) return "text-slate-400";
  if (score >= 80) return "text-emerald-400";
  if (score >= 60) return "text-amber-400";
  if (score >= 40) return "text-orange-400";
  return "text-rose-400";
}

function getReason(household: Household) {
  if (household.open_care_items !== null && household.open_care_items > 0) {
    return `${household.open_care_items} open care item(s)`;
  }

  if (household.attendance_rate !== null && household.attendance_rate < 60) {
    return `Attendance is ${household.attendance_rate}%`;
  }

  return "Household health needs review";
}

export function HouseholdsNeedingAttention({ households, unassessed = 0 }: Props) {
  if (households.length === 0) {
    return (
      <div className={`rounded-xl border p-5 ${unassessed ? "border-slate-800 bg-slate-900" : "border-emerald-500/20 bg-emerald-500/5"}`}>
        <div className={`flex items-center gap-2 ${unassessed ? "text-slate-300" : "text-emerald-300"}`}>
          <Home className="h-5 w-5" />
          <h2 className="font-semibold">Households Needing Attention</h2>
        </div>

        <p className="mt-2 text-sm text-slate-400">
          {unassessed ? `${unassessed} household(s) not yet assessed.` : "No assessed households currently need attention."}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
      <div className="mb-4 flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-400" />
        <h2 className="text-lg font-semibold text-white">
          Households Needing Attention
        </h2>
      </div>

      <div className="space-y-3">
        {households.map((household) => (
          <Link
            key={household.id}
            to={`/households/${household.id}`}
            className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950/70 p-4 transition hover:border-amber-500/60 hover:bg-slate-900"
          >
            <div>
              <p className="font-medium text-white">
                {household.household_name}
              </p>

              <p className="mt-1 text-sm text-slate-400">
                {getReason(household)}
              </p>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className={`text-xl font-bold ${healthColor(household.health_score)}`}>
                  {household.health_score}
                </p>
                <p className="text-xs text-slate-500">Health</p>
              </div>

              <ArrowRight className="h-4 w-4 text-slate-500" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

